/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type SxProps, Box, Typography, useTheme, type Palette } from '@mui/material';
import { type Theme as SystemTheme, getPath, alpha } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';
import { useContext, type ReactNode } from 'react';

import { ConditionalWrapper } from '../../../utils/ConditionalWrapper/ConditionalWrapper';
import type { SnippetsToggleProps } from './types';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';

export function SnippetsToggle<T>( {
	items,
	selectedItem,
	hasPremiumPlugins,
	onSelectionChange,
	toggleDisabledStatusCallback
}: SnippetsToggleProps<T> ): ReactNode {
	const { selectedTechnology, selectedInstallMethod } = useContext( InstallationSettingsContext );
	const { isTrialActive, hasSelfHostedAccess, hasCloudAccess, hasOldGPLAccess, isLoggedIn } = useContext( CustomerPortalContext );
	const { palette } = useTheme();

	return (
		<Box display='flex' gap={ '4px' } flexDirection={ 'column' }>
			<Box display='flex'>
				{ items.map( ( item, index ) => {
					const nextItem = items[ index + 1 ];

					const isSelected = item.key === selectedItem;
					const isNextItemSelected = nextItem ? nextItem.key === selectedItem : false;
					const { disabled, tooltip: disabledTooltip } = toggleDisabledStatusCallback( {
						key: item.key,
						selectedTechnology,
						selectedInstallMethod,
						hasPremiumPlugins,
						isLoggedIn,
						isTrialActive,
						hasCloudAccess,
						hasSelfHostedAccess,
						hasOldGPLAccess
					} );

					const backgroundColor = getBackgroundColor( disabled, isSelected, palette );
					const borderColor = isSelected ? 'primary.main' : 'secondary.100';
					const textColor = getTextColor( disabled, isSelected );

					const boxSx: SxProps<SystemTheme> = {
						display: 'flex',
						height: '34px',
						justifyContent: 'center',
						alignItems: 'center',
						padding: '0 7px',
						cursor: !disabled && !isSelected ? 'pointer' : 'default',
						backgroundColor,

						borderStyle: 'solid',
						borderWidth: '1px',
						whiteSpace: 'nowrap',
						borderColor,

						'&:hover': {
							backgroundColor: !disabled && !isSelected ?
								alpha( getPath( palette, textColor ) as string, .07 ) :
								backgroundColor
						}
					};

					/* c8 ignore start */

					if ( index === 0 ) {
						boxSx.borderTopLeftRadius = '4px';
						boxSx.borderBottomLeftRadius = '4px';
					} else {
						boxSx.borderLeftWidth = '0';
					}

					if ( index === items.length - 1 ) {
						boxSx.borderTopRightRadius = '4px';
						boxSx.borderBottomRightRadius = '4px';
					}

					if ( isNextItemSelected ) {
						boxSx.borderRightColor = 'primary.main';
					}

					/* c8 ignore stop */

					return (
						<ConditionalWrapper
							key={ item.key }
							condition={ disabled }
							wrapper={ children => (
								<Tooltip
									arrow
									disableInteractive
									title={ disabledTooltip }
								>
									<Box>{ children }</Box>
								</Tooltip>
							) }
						>
							<Box
								data-testid={ 'selection-' + item.key }
								onClick={ () => {
									if ( !disabled ) {
										onSelectionChange( item.key );
									}
								} }
								sx={ boxSx }
							>
								<Typography
									color={ textColor }
									fontWeight={ 500 }
									fontSize={ 14 }
									lineHeight='20px'
								>
									{ item.name }
								</Typography>
							</Box>
						</ConditionalWrapper>
					);
				} ) }
			</Box>
		</Box>
	);
}

function getBackgroundColor( disabled: boolean, isSelected: boolean, palette: Palette ) {
	if ( disabled ) {
		return alpha( palette.secondary[ 100 ] as string, 0.5 );
	}

	if ( isSelected ) {
		return 'primary.main';
	}

	return 'common.white';
}

function getTextColor( disabled: boolean, isSelected: boolean ) {
	if ( disabled ) {
		return 'secondary.400';
	}

	if ( isSelected ) {
		return 'text.contrast';
	}

	return 'text.primary';
}
